<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useConfirm } from 'primevue/useconfirm';
import Button from 'primevue/button';
import ConfirmDialog from 'primevue/confirmdialog';
import Dialog from 'primevue/dialog';
import Listbox from 'primevue/listbox';
import Menu from 'primevue/menu';
import type { MenuItem } from 'primevue/menuitem';
import {
  useContacts,
  useEndpoint,
  useFetch,
  useOpportunities,
  useStringFunctions,
  useAccounts,
  useIndustryCodes,
  useToastNotifications,
} from '@apparatix/composables';
import { useAccountStore } from '@apparatix/stores/useAccountStore';
import {
  type AssociationContact,
  type ContactLabel,
  type ContactSearchResult,
} from '@apparatix/types/Leads';
import Modal from '@apparatix/Components/Modal.vue';
import AssociationListItem from './AssociationListItem.vue';
import AssociationListContainer from './AssociationListContainer.vue';
import AccountLabelTag from './AccountLabelTag.vue';
import ContactTypeChip from './ContactTypeChip.vue';
import { UpdateAccountResponse } from '@apparatix/types/Account';
import Popover from 'primevue/popover';
import { ContactPayload } from '@apparatix/types/Contacts';
import { cloneDeep } from 'lodash';
import CreateAccount from '@apparatix/library/CreateAccount.vue';
import {
  Column as SectionColumn,
  Columns,
  Stack,
  Section,
} from '@apparatix/library/core';
import type { AccountAssociated } from '@apparatix/types/Account';
import { newAccountPayloadTransformer } from '@apparatix/schemas/Account';
import { ContactCard } from './ContactCard';
import LeadWizardContactForm from '@apparatix/leads/LeadWizardContactForm.vue';
import { wizardNewContact, type WizardNewContact, newContactPayloadTransformer } from '@apparatix/schemas/CreateLeadWizard';
import { createObjectFromSchema } from '@apparatix/schemas/Helpers';
import { PendingContact } from '@apparatix/schemas/Contact';
import MultiSelect from 'primevue/multiselect';
import Tag from 'primevue/tag';
import type { FlyoutNavigationContext, ForwardNavigable } from './types';

type ModelObject = {
  id: number;
  name: string;
  view: string;
};

type Emits = ForwardNavigable & {
  (e: 'post-activity', payload: { modelObject: ModelObject, typeId: number }): void;
};

const emit = defineEmits<Emits>();

const store = useAccountStore();
const confirm = useConfirm();
const { destroy } = useFetch();
const {
  createContact, mapSearchResultToAssociation, getContactLabelChipSeverity,
} = useContacts();
import { button as greenButtonTheme } from '@apparatix/theme/Green';
const { showError } = useToastNotifications();

const showAddContactModal = ref(false);
const showRelateAccountModal = ref(false);
const showContactTypeSelectDialog = ref(false);
const areContactsDirty = ref(false);
const activeContact = ref<AssociationContact>();

const isActiveContactPrimary = computed(() => activeContact.value?.contactLabels
  .some(lbl => lbl.contactLabelId === 1) ?? false);

const contactMenuItems = computed<MenuItem[]>(() => [
  {
    label: 'Edit contact types',
    icon: 'pi pi-tags',
    command: () => {
      showContactTypeSelectDialog.value = true;
    },
  },
  {
    label: 'Unassociate contact',
    icon: 'pi pi-user-minus',
    command: () => {
      showRemoveContactConfirm();
    },
  },
  {
    label: 'Copy account address to contact',
    icon: 'pi pi-arrow-down-right',
    disabled: (store.invoiceFieldsLocked || ! store.accountDetails.addresses[0]) && (isActiveContactPrimary.value),
    command: () => {
      copyAccountAddressToContact();
    },
  },
]);
const contactMenuRef = ref();
const typeSelectPopover = ref();
const { updateContact } = useContacts();
const { industryCodeRequired } = useIndustryCodes();

const copyAccountAddressToContact = () => {
  if (activeContact.value.fullAddress) {
    confirm.require({
      header: 'Notice',
      message: `This contact already has an address: ${activeContact.value.fullAddress}. Are you sure you want to overwrite?`,
      group: 'overwriteContactAddressConfirm',
      accept: () => confirmedCopyAddress(),
    });
    return;
  }
  confirmedCopyAddress();
};

const confirmedCopyAddress = () => {
  const contactDataPayload = { id: activeContact.value.id, addresses: [store.accountDetails.addresses[0]]};

  delete contactDataPayload.addresses[0].id;
  if (activeContact.value.addressId) {
    contactDataPayload.addresses[0].id = activeContact.value.addressId;
  }

  updateContact({ data: { contacts: [contactDataPayload]}});
};

const showRemoveContactConfirm = () => {
  if (activeContact.value.contactLabels.some(contact => contact.contactLabelId === 1)) {
    confirm.require({
      header: 'Notice',
      message: 'There must be a primary contact associated with this Account.',
      group: 'primaryContactDeny2',
      acceptLabel: 'Ok',
      rejectClass: '!hidden',
    });
    return;
  }
  confirm.require({
    header: 'Confirm',
    group: 'removeContactConfirm2',
    message: 'Are you sure you want to remove this contact from this Account?',
    acceptLabel: 'Yes',
    rejectLabel: 'Cancel',
    accept: async () => {
      const idx = store.accountAssociations.contacts?.findIndex(contact => contact.id === activeContact.value.id);
      if (idx !== -1) {
        store.accountAssociations.contacts.splice(idx, 1);
      }

      await destroy(`/crm/accounts/${store.accountDetails.id}/contact/${activeContact.value.id}`);
    },
  });
};

const contactsExcludingActive = computed(() => {
  if (!activeContact.value) return store.accountAssociations.contacts;
  return store.accountAssociations.contacts.filter(contact => contact.id !== activeContact.value.id);
});

const shouldDisableContactLabel = (label: ContactLabel) => {
  if (store.invoiceFieldsLocked && label.contactLabelId === 1) {
    return true;
  }

  // If it isn't the primary or bill to label allow multiple.
  if (label.contactLabelId !== 1 && label.contactLabelId !== 3) {
    return false;
  }

  const usedLabels = contactsExcludingActive.value.flatMap(contact => contact.contactLabels);
  const idx = usedLabels.findIndex(l => l.contactLabelId === label.contactLabelId);
  return idx !== -1;
};

const canSaveChanges = computed(() => {
  let primaryCount = 0;
  store.accountAssociations.contacts?.forEach(contact => {
    contact.contactLabels?.forEach(label => {
      if (label.contactLabelDisplayName === 'Primary') {
        primaryCount += 1;
      }
    });
  });

  return primaryCount === 1;
});

const saveContactChangeButtonSeverity = computed(() => {
  if (!areContactsDirty.value) return 'secondary';
  if (!canSaveChanges.value) return 'danger';
  return 'primary';
});

// This is a bit of a code duplication of the update account in useAccounts. Only use this for contact and label updates pls
const updateAccountEndpoint = useEndpoint<UpdateAccountResponse>({
  type: 'PUT',
  url: '/crm/accounts',
  successField: 'data',
  toast: { showOnSuccess: false },
  onSuccess: response => {
    areContactsDirty.value = false;
    store.accountDetails = response.data.accounts[0];
    if (store.selectedAccount && store.selectedAccount.c2Id === store.accountDetails.id) {
      store.selectedAccount.primaryContact = store.accountDetails.primaryContact;
    }
  },
});

const iconButtonPt = { root: { class: 'w-5 h-5 p-1 bg-transparent hover:bg-[var(--neutral-background)]' }};

const { isEmptyString } = useStringFunctions();

const getContactTitle = (contact: AssociationContact) => {
  let title = '';
  if (!isEmptyString(contact.firstName)) {
    title = contact.firstName;
  }
  if (!isEmptyString(contact.lastName) && !isEmptyString(contact.firstName)) {
    title = `${title} ${contact.lastName}`;
  } else if (!isEmptyString(contact.lastName) && isEmptyString(contact.firstName)) {
    title = contact.lastName;
  }

  if (!isEmptyString(title)) {
    return title;
  }

  return 'Unnamed Contact';
};

const getContactSubtitle = (contact: AssociationContact) => {
  if (!isEmptyString(contact.jobTitle)) {
    return contact.jobTitle;
  }

  return contact.c2Name ?? ' ';
};

const restoreAndCloseContactSearchModal = () => {
  store.accountAssociations.contacts = Object.values(contactsAtStartOfEditing.value);
  closeContactSearchModal();
};

const closeContactSearchModal = () => {
  showAddContactModal.value = false;
};

const availableContactLabels = computed<ContactLabel[]>(() => {
  const usedLabels = new Set(
    store.accountAssociations.contacts?.flatMap(contact =>
      Array.isArray(contact.contactLabels) && contact.id !== activeContact.value?.id
        ? contact.contactLabels.map(label => label.contactLabelId)
        : [],
    ),
  );

  return store.options.contactLabels?.filter(
    label => !usedLabels.has(label.contactLabelId),
  ) || [];
});

const activeContactLabels = computed({
  get() {
    if (!activeContact.value) return undefined;
    const contactIndex = store.accountAssociations.contacts.findIndex(ct => ct.id === activeContact.value.id);

    return store.accountAssociations.contacts[contactIndex].contactLabels;
  },
  set(value: ContactLabel[]) {
    if (!activeContact.value) return;
    const contactIndex = store.accountAssociations.contacts.findIndex(ct => ct.id === activeContact.value.id);
    store.accountAssociations.contacts[contactIndex].contactLabels = value;
  },
});

const selectedContact = ref<ContactSearchResult>();

watch(() => selectedContact.value, newValue => {
  if (!newValue || typeof newValue !== 'object') return;
  store.addContact(mapSearchResultToAssociation(newValue));
  selectedContact.value = undefined;
});

const onAssociateContacts = () => {
  if (!store.accountAssociations.contacts?.length) return;

  if (!canSaveChanges.value) {
    confirm.require({
      header: 'Notice',
      message: 'There must be a primary contact associated with this Account.',
      group: 'primaryContactDeny',
      acceptLabel: 'Ok',
      rejectClass: '!hidden',
    });
    return;
  }

  saveAssociations();

  closeContactSearchModal();
};

const onSaveContactClick = (e: MouseEvent) => {
  if (!canSaveChanges.value) {
    confirm.require({
      target: e.currentTarget as HTMLButtonElement,
      header: 'Notice',
      message: 'There must be a primary contact associated with this Account.',
      group: 'primaryContactDeny',
      acceptLabel: 'Ok',
      rejectClass: '!hidden',
    });
    return;
  }

  saveAssociations();
};

const saveAssociations = async () => {
  const payload = {
    data: {
      accounts: [
        {
          id: store.accountDetails.id,
          contacts: store.accountAssociations.contacts.map(contact => ({ id: contact.id, labelIds: contact.contactLabels?.map(label => label.contactLabelId) ?? []})),
        },
      ],
    },
  };
  await updateAccountEndpoint.makeRequest(payload);
};

const shouldDisableContactEmailButton = (contact: AssociationContact) => {
  if (!Array.isArray(contact?.addressEmails)) return true;
  return isEmptyString(contact.addressEmails[0]?.email);
};

const shouldDisableContactCallButton = (contact: AssociationContact) => {
  if (!Array.isArray(contact?.addressPhoneNumbers)) return true;
  return isEmptyString(contact.addressPhoneNumbers[0]?.number);
};

const onEmailContactClick = (contact: AssociationContact) => {
  if (!Array.isArray(contact.addressEmails)) return;
  const [record] = contact.addressEmails;
  if (isEmptyString(record?.email)) return;

  if (contact) {
    emit('post-activity', {
      modelObject: {
        id: contact.id,
        name: contact.fullName,
        view: 'contacts',
      },
      typeId: 1,
    });
  }

  window.open(`mailto:${record.email}`);
};

const onCallContactClick = (contact: AssociationContact) => {
  if (!Array.isArray(contact.addressPhoneNumbers)) return;
  const [record] = contact.addressPhoneNumbers;
  if (isEmptyString(record?.number)) return;

  if (contact) {
    emit('post-activity', {
      modelObject: {
        id: contact.id,
        name: contact.fullName,
        view: 'contacts',
      },
      typeId: 2,
    });
  }

  window.location.href = `tel:${record.number}`;
};

const onContactOptionsClick = (e: MouseEvent, contact: AssociationContact) => {
  activeContact.value = contact;
  contactMenuRef.value.toggle(e);
};

// todo: This currently has to load all leads and opportunities since we need the swimlanes and options from them. It would be nice to be able to split this out to a smaller API call.
const helper = useOpportunities();
helper.loadInitialState();

const contactsAtStartOfEditing = ref();

const clickAddContact = () => {
  contactsAtStartOfEditing.value = cloneDeep(store.accountAssociations.contacts);
  showAddContactModal.value = true;
};

const handleNavigateForward = (context: FlyoutNavigationContext) => {
  emit('navigate-forward', context);
};

let accountAssociationsOnModalOpen = [];

const openAccountConnectModal = () => {
  accountAssociationsOnModalOpen = JSON.parse(JSON.stringify(store.accountAssociations.accounts));
  showRelateAccountModal.value = true;
};

// Reset the state of data when closed without a save.
const cancelAccountConnectModal = () => {
  accountsToLink.value = [];
  store.accountAssociations.accounts = accountAssociationsOnModalOpen;
  closeAccountConnectModal();
};

const closeAccountConnectModal = () => {
  showRelateAccountModal.value = false;
};

const createAccountComponent = ref();
const accountsToLink = ref<any[]>([]);
const { createAccount, updateAccount } = useAccounts();

const onAccountSelected = (account) => {

  const newAccountKey = crypto.randomUUID();
  account.agencyId = store.accountDetails.id;
  account.key = newAccountKey;
  accountsToLink.value.push(account);

  const mappedAccount: AccountAssociated = {
    c2Id: account.id,
    c2Name: account.name,
    c2Status: 'Active',
    c2Type: account.displayType ?? 'Prospective Advertiser',
    c2RawType: account.type,
    addressPhoneNumbers: account.phoneNumbers ?? [],
    addressEmails: account.emails ?? [],
    relationships: ['Advertiser'],
    key: newAccountKey,
    ownerName: account.owner?.fullName ?? '',
  };
  store.accountAssociations.accounts.unshift(mappedAccount);
  createAccountComponent.value.reset();
};

const onCreateAccountCancel = () => {
  createAccountComponent.value.reset();
};

const onSaveAccountConnect = async () => {
  try {
    for (const account of accountsToLink.value) {
      if (!account.id) {
        const payload = newAccountPayloadTransformer.parse(account);
        const response = await createAccount(payload);

        // Once we get the data back find the data in the store for the account we added and give it the id so the click link works
        const accountToUpdate = store.accountAssociations.accounts.find(a => a.key === account.key);
        accountToUpdate.c2Id = response.id;
        accountToUpdate.c2Name = response.name;
      } else {
        // eslint-disable-next-line camelcase
        const payload = { data: { accounts: [{ id: account.id, agency_id: account.agencyId }]}};
        await updateAccount(payload);

        const accountToUpdate = store.accountAssociations.accounts.find(a => a.c2Id === account.id);
        accountToUpdate.c2Name = `${account.name}/${store.accountDetails.name}`;
      }
    }
    closeAccountConnectModal();
  } catch (err) {
    showError(err, 'Error associating accounts');
  }
};

const getNewContactSchema = () => wizardNewContact;

const contactFormModel = ref<WizardNewContact>(createObjectFromSchema(getNewContactSchema()));
const contactHasUnsavedEdits = ref(false);
const refreshContactModal = ref(0);

const addPendingContact = async (payload: PendingContact) => {
  let contact: AssociationContact;
  if (! payload.id) {
    const contactPayload = newContactPayloadTransformer.parse(payload);
    const result = await createContact(contactPayload as ContactPayload);

    if (! result?.id) {
      throw new Error(`Error saving ${payload.fullName} as new Contact`);
    }

    contact = {
      id: result.id,
      firstName: result.contact.first_name,
      lastName: result.contact.last_name,
      contactLabels: [],
      companyName: result.contact.company_name,
      c2Name: result.contact.company_name,
    };
  } else {
    contact = {
      id: payload.id,
      firstName: payload.firstName,
      lastName: payload.lastName,
      contactLabels: [],
      companyName: payload.companyName,
      c2Name: payload.companyName,
    };
  }


  if (payload.emails?.length) {
    contact.addressEmails = payload.emails;
  }

  if (payload.phoneNumbers?.length) {
    contact.addressPhoneNumbers = payload.phoneNumbers;
  }

  store.accountAssociations.contacts.push(contact);
  contactFormModel.value = createObjectFromSchema(getNewContactSchema());

  refreshContactModal.value += 1;
};

const onContactFormCancel = () => contactFormModel.value = createObjectFromSchema(getNewContactSchema());

const removeContact = (remove: WizardNewContact) => {
  store.accountAssociations.contacts = store.accountAssociations.contacts.filter(contact => contact.id !== remove.id);
};

</script>
<template>
  <div class="w-full flex flex-col gap-6">
    <Section class="mb-3" :footer="false">
      <AssociationListContainer
        title="Contacts"
        :item-count="store.accountAssociations.contacts?.length ?? 0"
        @on:add-click="clickAddContact"
      >
        <AssociationListItem
          v-for="contact in store.accountAssociations.contacts"
          :id="contact.id"
          :key="contact.id"
          tab-id="relationships"
          association-type="contact"
          :title="getContactTitle(contact)"
          :subtitle="getContactSubtitle(contact)"
          :border="true"
          @navigate-forward="handleNavigateForward"
        >
          <div class="flex gap-3 items-center justify-end">
            <Button
              severity="secondary"
              icon="pi pi-envelope"
              size="small"
              text
              rounded
              :pt="iconButtonPt"
              :disabled="shouldDisableContactEmailButton(contact)"
              @click="onEmailContactClick(contact)"
            />
            <Button
              severity="secondary"
              icon="pi pi-phone"
              size="small"
              class="md:hidden"
              text
              rounded
              :pt="iconButtonPt"
              :disabled="shouldDisableContactCallButton(contact)"
              @click="onCallContactClick(contact)"
            />
            <Button
              severity="secondary"
              icon="pi pi-ellipsis-v"
              size="small"
              text
              rounded
              :pt="iconButtonPt"
              @click="e => onContactOptionsClick(e, contact)"
            />
          </div>
          <template #footer>
            <div class="w-full flex items-center gap-2" :class="[contact.contactLabels?.length > 0 ? 'pb-2 md:pb-4': '']">
              <ContactTypeChip v-for="label in contact.contactLabels" :key="label.contactLabelId" :label="label" />
            </div>
          </template>
        </AssociationListItem>
        <div class="flex items-center justify-end">
          <Button
            v-show="areContactsDirty"
            label="Save"
            size="small"
            :dt="greenButtonTheme"
            :severity="saveContactChangeButtonSeverity"
            @click="onSaveContactClick"
          />
        </div>
      </AssociationListContainer>
    </Section>
    <Section class="mb-3" :footer="false">
      <AssociationListContainer
        title="Accounts"
        :item-count="store.accountAssociations.accounts?.length ?? 0"
        :hide-add-button="true"
        :show-associate-account-button="store.hasLinkAccountButton && store.accountDetails.rawTypes.includes('Agency')"
        @on:connect-click="openAccountConnectModal"
      >
        <AssociationListItem
          v-for="account in store.accountAssociations.accounts"
          :id="account.c2Id"
          :key="account.c2Id"
          tab-id="relationships"
          association-type="account"
          :title="account.c2Name"
          :subtitle="account.c2Type"
          :border="true"
          @navigate-forward="handleNavigateForward"
        >
          <template #footer>
            <div class="w-full flex items-center gap-2" :class="[(account.relationships?.length > 0 || account.accountLabel?.value) ? 'pb-2 md:pb-4': '']">
              <ContactTypeChip
                v-for="label in account.relationships"
                :key="label"
                :label="{contactLabelId: 0, contactLabelDisplayName: label}"
              />
              <AccountLabelTag :label="account.accountLabel" />
            </div>
          </template>
        </AssociationListItem>
      </AssociationListContainer>
    </Section>
  </div>
  <Modal
    :visible="showAddContactModal"
    title="Search for contacts"
    accept-label="Save"
    @close="restoreAndCloseContactSearchModal"
    @accept="onAssociateContacts"
  >
    <template #body>
      <div class="p-4">
        <div class="flex flex-col lg:flex-row-reverse gap-4 lg:gap-x-6 w-full">
          <div class="flex-initial flex flex-col gap-3 w-full lg:w-1/2">
            <h2 class="font-medium text-sm text-[var(--swimlane-secondary-text)]">
              Contacts
            </h2>
            <div v-if="store.accountAssociations.contacts.length" class="flex flex-col gap-4">
              <ContactCard
                v-for="contact in store.accountAssociations.contacts"
                :id="contact.id"
                :key="contact.id"
                :name="getContactTitle(contact)"
                :company="getContactSubtitle(contact)"
                :owner="contact.ownerName"
                :closable="true"
                @on-close="removeContact(contact)"
              >
                <Stack :gap="2">
                  <div class="flex items-center">
                    <MultiSelect
                      v-model="contact.contactLabels"
                      :options="store.options.contactLabels"
                      option-label="contactLabelDisplayName"
                      placeholder="Select Contact Type"
                      display="chip"
                      size="small"
                      :show-toggle-all="false"
                      fluid
                      :option-disabled="shouldDisableContactLabel"
                      @before-show="() => (activeContact = contact)"
                    >
                      <template #chip="slotProps">
                        <Tag :severity="getContactLabelChipSeverity(slotProps.value)" :value="slotProps.value.contactLabelDisplayName" />
                      </template>
                    </MultiSelect>
                  </div>
                </Stack>
              </ContactCard>
            </div>
          </div>
          <div class="flex-initial flex flex-col gap-4 w-full lg:w-1/2">
            <LeadWizardContactForm
              v-model="contactFormModel"
              :use-dynamic-company-search="false"
              :use-dynamic-name-search="true"
              :is-associated-with-existing-account="false"
              :allow-agency-in-wizard="false"
              :address="store.accountDetails.addresses[0]"
              :company-name="store.accountDetails.name"
              :restore-prefilled-fields="refreshContactModal"
              @on:create="addPendingContact"
              @contact-selected="addPendingContact"
              @cancel="onContactFormCancel"
              @on:dirty-change="dirty => contactHasUnsavedEdits = dirty"
            />
          </div>
        </div>
      </div>
      <Popover ref="typeSelectPopover">
        <div class="flex w-40">
          <Listbox
            v-model="activeContactLabels"
            class="w-full"
            :options="store.options.contactLabels"
            :option-disabled="shouldDisableContactLabel"
            option-label="contactLabelDisplayName"
            multiple
          />
        </div>
      </Popover>
    </template>
  </Modal>
  <Modal
    :visible="showRelateAccountModal"
    title="Link Accounts"
    accept-label="Save"
    @close="cancelAccountConnectModal"
    @accept="onSaveAccountConnect"
  >
    <template #body>
      <div class="p-4">
        <Columns>
          <SectionColumn>
            <CreateAccount
              ref="createAccountComponent"
              :default-type="'Advertiser'"
              :disable-account-search="false"
              :allowed-selection-types="['Advertiser', 'Lead']"
              :allowed-creation-types="['Advertiser']"
              :is-loading="store.isLoading"
              :agency-id="store.accountDetails.id"
              :industry-code-required="industryCodeRequired"
              hide-title
              externally-manage-account-limits
              show-cancel-button
              :allow-agency-in-wizard="false"
              :disallow-accounts-with-agencies="true"
              @on:account-selected="onAccountSelected"
              @on:submit="onAccountSelected"
              @on:cancel="onCreateAccountCancel"
            />
          </SectionColumn>
          <SectionColumn>
            <div class="flex flex-col p-3 rounded-xl bg-[#F1F5F9]">
              <Stack :gap="4">
                <ContactCard
                  v-for="account in store.accountAssociations.accounts"
                  :id="account.c2Id"
                  :key="account.c2Id"
                  :name="account.c2Name"
                  :company="account.c2Type"
                  :owner="account.ownerName"
                />
              </Stack>
            </div>
          </SectionColumn>
        </Columns>
      </div>
      <Popover ref="typeSelectPopover">
        <div class="flex w-40">
          <Listbox
            v-model="activeContactLabels"
            class="w-full"
            :options="availableContactLabels"
            :option-disabled="shouldDisableContactLabel"
            option-label="contactLabelDisplayName"
            multiple
          />
        </div>
      </Popover>
    </template>
  </Modal>
  <Menu ref="contactMenuRef" :model="contactMenuItems" :popup="true" />
  <ConfirmDialog group="primaryContactDeny" />
  <ConfirmDialog group="primaryContactDeny2" />
  <ConfirmDialog group="removeContactConfirm2" />
  <ConfirmDialog group="overwriteContactAddressConfirm" />
  <Dialog v-model:visible="showContactTypeSelectDialog" modal header="Edit contact types">
    <Listbox
      v-model="activeContact.contactLabels"
      multiple
      :options="store.options.contactLabels"
      :option-disabled="shouldDisableContactLabel"
      option-label="contactLabelDisplayName"
      class="w-full"
      @update:model-value="areContactsDirty = true"
    />
  </Dialog>
</template>
